import { PropertyChangedEventArgs, Observable } from "../../events";
import { ValidableBase } from "../../validable";
import { Cartesian2 } from "../../geometry";
import { IsLocation, Bearing, Geo2 } from "../../geography";
import { TileSystemBounds } from "../tiles.system";
import { TileNavigationStateSynchronizer } from "./tiles.navigation.state.sync";
export class TileNavigationState extends ValidableBase {
    static GetLodScale(lod) {
        let lodOffset = (lod * 1000 - Math.round(lod) * 1000) / 1000;
        let scale = lodOffset < 0 ? 1 + lodOffset / 2 : 1 + lodOffset;
        return scale;
    }
    constructor(center, lod, azimuth, bounds) {
        super();
        this._cartesianCache = Cartesian2.Zero();
        this._lodf = lod ?? 0;
        this._center = center ? (IsLocation(center) ? new Geo2(center?.lat ?? 0, center?.lon ?? 0) : new Geo2(center[0] ?? 0, center[1] ?? 0)) : Geo2.Zero();
        this._azimuth = new Bearing(azimuth ?? 0);
        this._bounds = bounds ?? new TileSystemBounds();
        this._boundsObserver = this._bounds.propertyChangedObservable.add(this._boundsPropertyChanged.bind(this));
        this._lod = Math.round(this._lodf);
        this._scale = TileNavigationState.GetLodScale(this._lodf);
        this._sync = null;
    }
    clone() {
        return new TileNavigationState(this._center, this._lod, this._azimuth.value, this._bounds);
    }
    dispose() {
        this._boundsObserver?.disconnect;
        this._boundsObserver = null;
        if (this._sync) {
            this._sync.dispose();
            this._sync = null;
        }
    }
    get lod() {
        return this._lod;
    }
    get scale() {
        return this._scale;
    }
    get center() {
        return this._center;
    }
    set center(center) {
        center = center ?? Geo2.Zero();
        const lat = Math.min(Math.max(center.lat, this._bounds.minLatitude), this._bounds.maxLatitude);
        const lon = Math.min(Math.max(center.lon, this._bounds.minLongitude), this._bounds.maxLongitude);
        if (this._center.lat != lat || this._center.lon != lon) {
            if (this._propertyChangedObservable?.hasObservers()) {
                const old = this._center.clone();
                this._center.lat = lat;
                this._center.lon = lon;
                const e = new PropertyChangedEventArgs(this, old, this._center.clone(), TileNavigationState.CENTER_PROPERTY_NAME);
                this._propertyChangedObservable.notifyObservers(e, -1, this, this);
                this.invalidate();
                return;
            }
            this._center.lat = lat;
            this._center.lon = lon;
            this.invalidate();
        }
    }
    get zoom() {
        return this._lodf;
    }
    set zoom(lodf) {
        lodf = Math.min(Math.max(lodf, this._bounds.minLOD), this._bounds.maxLOD);
        if (this._lodf != lodf) {
            const old = this._lodf;
            this._lodf = lodf;
            const lod = Math.round(this._lodf);
            let event = null;
            if (this._lod != lod) {
                const oldLod = this._lod;
                this._lod = lod;
                if (this._propertyChangedObservable && this._propertyChangedObservable.hasObservers()) {
                    event = new PropertyChangedEventArgs(this, oldLod, this._lod, TileNavigationState.LOD_PROPERTY_NAME);
                }
            }
            this._scale = TileNavigationState.GetLodScale(this._lodf);
            this.invalidate();
            if (this._propertyChangedObservable && this._propertyChangedObservable.hasObservers()) {
                if (event) {
                    this._propertyChangedObservable.notifyObservers(event, -1, this, this);
                }
                event = new PropertyChangedEventArgs(this, old, this._lodf, TileNavigationState.ZOOM_PROPERTY_NAME);
                this._propertyChangedObservable.notifyObservers(event, -1, this, this);
            }
        }
    }
    get azimuth() {
        return this._azimuth;
    }
    set azimuth(r) {
        if (this._azimuth.value != r.value) {
            const old = this._azimuth;
            this._azimuth = r;
            this.invalidate();
            if (this._propertyChangedObservable?.hasObservers()) {
                const e = new PropertyChangedEventArgs(this, old, this._azimuth, TileNavigationState.AZIMUTH_PROPERTY_NAME);
                this._propertyChangedObservable.notifyObservers(e, -1, this, this);
            }
        }
    }
    get camera() {
        return this._camera;
    }
    set camera(c) {
        if (this._camera !== c) {
            const old = this._camera;
            this._camera = c;
            this.invalidate();
            if (this._propertyChangedObservable?.hasObservers()) {
                const e = new PropertyChangedEventArgs(this, old, this._camera, TileNavigationState.CAMERA_PROPERTY_NAME);
                this._propertyChangedObservable.notifyObservers(e, -1, this, this);
            }
        }
    }
    get bounds() {
        return this._bounds;
    }
    set bounds(bounds) {
        if (this._bounds !== bounds) {
            this._boundsObserver?.disconnect;
            const old = this._bounds;
            this._bounds = bounds;
            this._boundsObserver = this._bounds.propertyChangedObservable.add(this._boundsPropertyChanged.bind(this));
            this.invalidate();
            if (this._propertyChangedObservable?.hasObservers()) {
                const e = new PropertyChangedEventArgs(this, old, this._bounds, TileNavigationState.BOUNDS_PROPERTY_NAME);
                this._propertyChangedObservable.notifyObservers(e, -1, this, this);
            }
        }
    }
    get propertyChangedObservable() {
        if (!this._propertyChangedObservable)
            this._propertyChangedObservable = new Observable();
        return this._propertyChangedObservable;
    }
    copy(other) {
        this.center = other.center;
        this.azimuth = new Bearing(other.azimuth.value);
        this.zoom = other.zoom;
        return this;
    }
    syncWith(state) {
        if (this._sync) {
            this._sync.dispose();
            this._sync = null;
        }
        if (state) {
            this.copy(state).validate();
            this._sync = new TileNavigationStateSynchronizer(state, this);
        }
        return this;
    }
    toString() {
        return `center: ${this.center}, zoom: ${this.zoom}, azimuth: ${this.azimuth}`;
    }
    _boundsPropertyChanged(e, state) { }
}
TileNavigationState.CENTER_PROPERTY_NAME = "center";
TileNavigationState.ZOOM_PROPERTY_NAME = "zoom";
TileNavigationState.LOD_PROPERTY_NAME = "lod";
TileNavigationState.AZIMUTH_PROPERTY_NAME = "azimuth";
TileNavigationState.BOUNDS_PROPERTY_NAME = "bounds";
TileNavigationState.CAMERA_PROPERTY_NAME = "camera";
//# sourceMappingURL=tiles.navigation.state.js.map